import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import {
  Subject,
  Subscription,
  catchError,
  finalize,
  firstValueFrom,
  of,
  switchMap,
} from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DrawingStorageFile,
  StorageLinkedQuoteItem,
  splitStorageFilename,
} from '../../../models/storage.model';
import { StorageService } from '../../services/storage.service';
import { CustomersService } from '../../services/customers.service';
import { SelectModule } from 'primeng/select';
import { PdfViewerComponent } from '../../shared/pdf-viewer/pdf-viewer.component';
import { ButtonModule } from 'primeng/button';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { debouncedSearchTerms } from '../../../utils/list-search';
import { DownloadService } from '../../services/download.service';

/** The drawing PDFs tab of the Storage page. */
@Component({
  selector: 'app-drawing-storage-tab',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DatePipe,
    ButtonModule,
    SharedConfirmDialogComponent,
    DialogModule,
    ProgressSpinnerModule,
    Tooltip,
    Paginator,
    PdfViewerComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './drawing-storage-tab.component.html',
})
export class DrawingStorageTabComponent implements OnInit, OnDestroy {
  storageService = inject(StorageService);
  confirmationService = inject(ConfirmationService);
  router = inject(Router);
  downloadService = inject(DownloadService);
  customersService = inject(CustomersService);
  files: DrawingStorageFile[] = [];
  loading = true;
  loadError: string | null = null;
  totalRecords = 0;
  rows = 20;
  first = 0;

  viewDialogVisible = false;
  viewDialogLoading = false;
  selectedFile: DrawingStorageFile | null = null;
  selectedFileUrl: string | null = null;

  renameTarget: DrawingStorageFile | null = null;
  renameExtension = '';
  renameControl = new FormControl('', { nonNullable: true });
  renameInProgress = false;
  renameError: string | null = null;

  selectedCompanyFilter: number | 'none' | null = null;
  companyFilterOptions: Array<{
    label: string;
    value: number | 'none' | null;
  }> = [
    { label: 'All customers', value: null },
    { label: 'No customer', value: 'none' },
  ];

  searchQuery = new FormControl('', { nonNullable: true });
  activeSearch = '';

  private fileRequests = new Subject<boolean>();
  private subscriptions = new Subscription();
  private viewSubscription?: Subscription;

  ngOnInit(): void {
    this.subscriptions.add(
      this.fileRequests
        .pipe(
          switchMap((showLoadingState) => {
            if (showLoadingState) {
              this.loading = true;
            }
            return this.storageService
              .list('drawing', {
                limit: this.rows,
                offset: this.first,
                company: this.selectedCompanyFilter,
                search: this.activeSearch,
              })
              .pipe(
                catchError((err) => {
                  console.error('Error fetching drawings', err);
                  return of(null);
                }),
              );
          }),
        )
        .subscribe((response) => {
          this.loading = false;
          this.files = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.loadError = response ? null : 'Failed to load drawings.';
        }),
    );

    this.subscriptions.add(
      debouncedSearchTerms(this.searchQuery.valueChanges).subscribe(
        (search) => {
          this.activeSearch = search;
          this.first = 0;
          this.loadFiles(true);
        },
      ),
    );

    this.subscriptions.add(
      this.customersService.companies$.subscribe((companies) => {
        this.companyFilterOptions = [
          { label: 'All customers', value: null },
          { label: 'No customer', value: 'none' },
          ...companies.map((company) => ({
            label: company.name,
            value: company.id,
          })),
        ];
      }),
    );
    void this.customersService.loadCompanies().catch((error) => {
      console.error('Error loading companies', error);
    });

    this.loadFiles(true);
  }

  onCompanyFilterChange(value: number | 'none' | null): void {
    this.selectedCompanyFilter = value;
    this.first = 0;
    this.loadFiles(true);
  }

  loadFiles(showLoadingState: boolean): void {
    this.fileRequests.next(showLoadingState);
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.loadFiles(true);
  }

  private reloadAfterDelete(): void {
    // Deleting the last row of a later page would otherwise leave it empty.
    if (this.files.length === 1 && this.first > 0) {
      this.first = Math.max(0, this.first - this.rows);
    }
    this.loadFiles(false);
  }

  reuseWarning(file: DrawingStorageFile): string | null {
    return file.reuse_blocked_reason
      ? `Can't be used as an existing drawing: ${file.reuse_blocked_reason}`
      : null;
  }

  openViewDialog(file: DrawingStorageFile): void {
    // Cancel any earlier detail request so a late response cannot replace this one.
    this.viewSubscription?.unsubscribe();
    this.selectedFile = file;
    this.viewDialogVisible = true;
    this.viewDialogLoading = true;
    this.selectedFileUrl = null;

    this.viewSubscription = this.storageService
      .get('drawing', file)
      .pipe(
        finalize(() => {
          this.viewDialogLoading = false;
        }),
      )
      .subscribe({
        next: (detail) => (this.selectedFileUrl = detail.file_url),
        error: (error) => {
          console.error('Error loading drawing for viewer:', error);
        },
      });
    this.subscriptions.add(this.viewSubscription);
  }

  closeViewDialog(): void {
    this.viewSubscription?.unsubscribe();
    this.viewDialogVisible = false;
    this.selectedFile = null;
    this.selectedFileUrl = null;
  }

  openRenameDialog(file: DrawingStorageFile): void {
    this.renameTarget = file;
    // The extension is shown after the input and cannot be edited.
    const { stem, extension } = splitStorageFilename(file.filename);
    this.renameExtension = extension;
    this.renameControl.setValue(stem);
    this.renameError = null;
  }

  closeRenameDialog(): void {
    this.renameTarget = null;
    this.renameInProgress = false;
    this.renameError = null;
  }

  submitRename(): void {
    const target = this.renameTarget;
    const stem = this.renameControl.value.trim();
    if (!target || !stem || this.renameInProgress) {
      return;
    }

    this.renameInProgress = true;
    this.renameError = null;
    this.subscriptions.add(
      this.storageService
        .rename('drawing', target, stem + this.renameExtension)
        .subscribe({
          next: () => {
            this.closeRenameDialog();
            // The renamed file may no longer match, which could empty this page.
            if (this.activeSearch) {
              this.first = 0;
            }
            this.loadFiles(false);
          },
          error: () => {
            this.renameInProgress = false;
            this.renameError = 'Failed to rename file. Please try again.';
          },
        }),
    );
  }

  confirmDelete(file: DrawingStorageFile): void {
    if (!file.can_delete) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Delete File',
      message: `Are you sure you want to delete "${file.filename}"? This action is irreversible.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.subscriptions.add(
          this.storageService.delete('drawing', file).subscribe({
            next: () => {
              this.reloadAfterDelete();
            },
            error: (err) => console.error('Error deleting drawing', err),
          }),
        );
      },
    });
  }

  navigateToQuote(linkedItem: StorageLinkedQuoteItem): void {
    const revisionNumber = linkedItem.linked_revision_number;
    this.router.navigate(['/quote', linkedItem.quote_id, revisionNumber]);
  }

  getLinkedQuoteItemTooltip(linkedItem: StorageLinkedQuoteItem): string {
    return `Linked to ${linkedItem.linked_quote_item_name} in Revision ${linkedItem.linked_revision_number} of Quote ${linkedItem.quote_code_number}`;
  }

  async downloadFile(file: DrawingStorageFile): Promise<void> {
    try {
      const detail = await firstValueFrom(
        this.storageService.get('drawing', file),
      );
      if (!detail.file_url) {
        throw new Error('File has no stored content to download');
      }
      await this.downloadService.downloadFile(detail.file_url, {
        defaultFilename: file.filename,
        mimeType: 'application/pdf',
      });
    } catch (error) {
      console.error('Error downloading drawing', error);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
